import React, { useEffect, useState } from 'react';
import DailyIframe, { DailyCall } from '@daily-co/daily-js';
import { DailyProvider, useDaily, useParticipantIds } from '@daily-co/daily-react';
import { VideoTile } from './VideoTile';
import { ControlBar } from './ControlBar';
import { ConnectionStatus } from './ConnectionStatus';
import { WaitingRoom } from './WaitingRoom';

interface VideoRoomProps {
  roomUrl: string;
  token: string;
  userName: string;
  onLeave: () => void;
  onError?: (error: Error) => void;
  showControls?: boolean;
  isHost?: boolean;
}

/**
 * Main video room component using Daily.co
 * Wraps the video experience with DailyProvider
 */
export const VideoRoom: React.FC<VideoRoomProps> = ({
  roomUrl,
  token,
  userName,
  onLeave,
  onError,
  showControls = true,
  isHost = false,
}) => {
  const [callObject, setCallObject] = useState<DailyCall | null>(null);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    // Create Daily call object
    const daily = DailyIframe.createCallObject({
      url: roomUrl,
      token: token,
      userName: userName,
      audioSource: true,
      videoSource: true,
    });

    setCallObject(daily);

    // Join the call
    daily
      .join()
      .then(() => {
        console.log('Successfully joined Daily call');
      })
      .catch((err) => {
        console.error('Error joining Daily call:', err);
        setError(err);
        onError?.(err);
      });

    // Cleanup on unmount
    return () => {
      daily.leave().then(() => {
        daily.destroy();
      });
    };
  }, [roomUrl, token, userName, onError]);

  if (error) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-900">
        <div className="text-center text-white">
          <h2 className="text-2xl font-bold mb-4">Connection Error</h2>
          <p className="text-gray-300 mb-6">{error.message}</p>
          <button
            onClick={onLeave}
            className="px-6 py-2 bg-red-600 hover:bg-red-700 rounded-lg transition"
          >
            Leave Session
          </button>
        </div>
      </div>
    );
  }

  if (!callObject) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-900">
        <div className="text-white">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
          <p>Connecting to session...</p>
        </div>
      </div>
    );
  }

  return (
    <DailyProvider callObject={callObject}>
      <VideoRoomContent
        onLeave={onLeave}
        showControls={showControls}
        isHost={isHost}
      />
    </DailyProvider>
  );
};

interface VideoRoomContentProps {
  onLeave: () => void;
  showControls: boolean;
  isHost: boolean;
}

/**
 * Inner component that uses Daily hooks
 * Must be wrapped by DailyProvider
 */
const VideoRoomContent: React.FC<VideoRoomContentProps> = ({
  onLeave,
  showControls,
  isHost,
}) => {
  const daily = useDaily();
  const participantIds = useParticipantIds();
  const [connectionState, setConnectionState] = useState<string>('connecting');

  useEffect(() => {
    if (!daily) return;

    const handleMeetingStateChange = (event?: any) => {
      const state = daily.meetingState();
      setConnectionState(state);
    };

    // Listen for meeting state changes
    daily.on('joined-meeting', handleMeetingStateChange);
    daily.on('left-meeting', handleMeetingStateChange);
    daily.on('error', handleMeetingStateChange);

    // Get initial state
    handleMeetingStateChange();

    return () => {
      daily.off('joined-meeting', handleMeetingStateChange);
      daily.off('left-meeting', handleMeetingStateChange);
      daily.off('error', handleMeetingStateChange);
    };
  }, [daily]);

  const handleLeave = async () => {
    if (daily) {
      await daily.leave();
    }
    onLeave();
  };

  // Show waiting room if not joined yet
  if (connectionState === 'joining-meeting' || connectionState === 'loading') {
    return <WaitingRoom />;
  }

  if (connectionState !== 'joined-meeting') {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-900">
        <div className="text-center text-white">
          <h2 className="text-2xl font-bold mb-4">Connection Issue</h2>
          <p className="text-gray-300 mb-6">
            Unable to connect to the session. Please try again.
          </p>
          <button
            onClick={handleLeave}
            className="px-6 py-2 bg-red-600 hover:bg-red-700 rounded-lg transition"
          >
            Leave
          </button>
        </div>
      </div>
    );
  }

  // Filter out local participant
  const localId = daily?.participants()?.local?.user_id;
  const remoteParticipantIds = participantIds.filter((id) => id !== 'local');

  return (
    <div className="flex flex-col h-screen bg-gray-900">
      {/* Connection status indicator */}
      <ConnectionStatus state={connectionState} />

      {/* Video grid */}
      <div className="flex-1 p-4 overflow-auto">
        {participantIds.length === 0 ? (
          <WaitingRoom message="Waiting for other participant..." />
        ) : (
          <div className="h-full grid gap-4" style={{
            gridTemplateColumns: participantIds.length === 1 ? '1fr' : 'repeat(auto-fit, minmax(400px, 1fr))',
          }}>
            {participantIds.map((participantId) => (
              <VideoTile
                key={participantId}
                participantId={participantId}
                isLocal={participantId === 'local'}
              />
            ))}
          </div>
        )}
      </div>

      {/* Control bar */}
      {showControls && (
        <ControlBar onLeave={handleLeave} isHost={isHost} />
      )}
    </div>
  );
};
